import React, { useEffect, useState } from "react";
import { Star, UtensilsCrossed, Bike } from "lucide-react";
import { PageHeader, EmptyState, LoadingBlock, StatCard } from "../../components/app/ui";
import { useMyRestaurant } from "./RestaurantOrders";
import RestaurantProfileForm from "../../components/app/RestaurantProfileForm";
import { restaurantApi, formatDate } from "../../lib/api";

function Stars({ value }) {
  return (
    <span className="inline-flex">
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-4 w-4 ${i <= Math.round(value || 0) ? "fill-brand text-brand" : "text-surface-300"}`} />)}
    </span>
  );
}

export default function Ratings() {
  const [restaurant, setRestaurant] = useMyRestaurant();
  const [data, setData] = useState(null);
  useEffect(() => { if (restaurant) restaurantApi.ratings().then(setData).catch(() => setData({ count: 0, ratings: [] })); }, [restaurant]);

  if (restaurant === undefined) return <><PageHeader title="Ratings" /><LoadingBlock rows={3} /></>;
  if (restaurant === null) return <><PageHeader title="Set up your restaurant" description="Create your restaurant profile first." /><div className="max-w-3xl"><RestaurantProfileForm onSaved={setRestaurant} title="Create your restaurant" /></div></>;

  const dist = data?.distribution || {};
  const maxBar = Math.max(1, ...Object.values(dist));

  return (
    <>
      <PageHeader title="Ratings & reviews" description="How customers rate your restaurant, food and delivery." />
      {data === null && <LoadingBlock rows={3} />}
      {data && (
        <>
          <div className="mb-6 grid gap-4 sm:grid-cols-3">
            <StatCard label="Overall" value={data.avg_rating ? `${data.avg_rating} ★` : "—"} hint={`${data.count} ratings`} icon={Star} tone="brand" />
            <StatCard label="Food" value={data.avg_food ? `${data.avg_food} ★` : "—"} icon={UtensilsCrossed} />
            <StatCard label="Delivery" value={data.avg_delivery ? `${data.avg_delivery} ★` : "—"} icon={Bike} tone="graphite" />
          </div>
          {data.count > 0 && (
            <div className="kt-card mb-6 p-5" data-testid="rating-distribution">
              {[5, 4, 3, 2, 1].map((s) => (
                <div key={s} className="flex items-center gap-3 py-1 text-sm">
                  <span className="w-10 text-ink-soft">{s} ★</span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-surface-200"><div className="h-full rounded-full bg-brand" style={{ width: `${((dist[s] || 0) / maxBar) * 100}%` }} /></div>
                  <span className="w-8 text-right text-ink-muted">{dist[s] || 0}</span>
                </div>
              ))}
            </div>
          )}
          {data.count === 0 && <EmptyState icon={Star} title="No ratings yet" text="Ratings appear here once customers review their delivered orders." />}
          <div className="grid gap-3" data-testid="ratings-list">
            {(data.ratings || []).map((r) => (
              <div key={r.order_id} className="kt-card p-4" data-testid={`rating-${r.order_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2"><Stars value={r.rating} /><span className="font-semibold text-ink">{r.customer_name}</span></div>
                  <span className="text-xs text-ink-muted">{formatDate(r.rated_at)}</span>
                </div>
                <div className="mt-1 flex gap-4 text-xs text-ink-soft">
                  {r.food_rating ? <span className="inline-flex items-center gap-1"><UtensilsCrossed className="h-3 w-3" /> Food {r.food_rating}★</span> : null}
                  {r.delivery_rating ? <span className="inline-flex items-center gap-1"><Bike className="h-3 w-3" /> Delivery {r.delivery_rating}★</span> : null}
                </div>
                {r.review && <p className="mt-2 rounded-xl bg-surface-100 p-3 text-sm text-ink">{r.review}</p>}
                <p className="mt-1 text-xs text-ink-muted">{r.items.join(", ")}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
